import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { ChartBarIcon, ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { IDashboardStats } from "../../../models/DashboardStats";
import DashboardTile from "../../dashboard/DashboardTile";
import LoadingWheel from "../LoadingWheel";
import { ACTIVITY_METRICS, ActivityMetricKey, getMetricValue } from "./activityMetrics";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        // Counts are whole numbers, so hide any fractional gridline labels.
        callback: function (val: number | string) {
          return Number.isInteger(Number(val)) ? val : "";
        },
      },
      grid: { display: false },
    },
    x: {
      grid: { display: false },
    },
  },
};

interface IPerformanceUserActivityGraphProps {
  title: string;
  stats?: IDashboardStats;
  isLoading?: boolean;
  selectedMetric: ActivityMetricKey;
  onMetricChange: (key: ActivityMetricKey) => void;
}

export default function PerformanceUserActivityGraph({
  title,
  stats,
  isLoading = false,
  selectedMetric,
  onMetricChange,
}: IPerformanceUserActivityGraphProps) {
  const users = useMemo(
    () =>
      Object.keys(stats?.userActivity ?? {})
        .filter((key) => key !== "Total")
        .sort((a, b) => a.localeCompare(b)),
    [stats]
  );

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [hideZero, setHideZero] = useState(false);

  // Default to showing every user, and re-select them whenever the user list changes.
  useEffect(() => {
    setSelectedUsers(users);
  }, [users]);

  // Read the brand colour from the Tailwind CSS variable so bars match the theme.
  const primaryColor = useMemo(() => {
    if (typeof window === "undefined") return "#007282";
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue("--primary-600")
      .trim();
    return value || "#007282";
  }, []);

  const metric = useMemo(
    () => ACTIVITY_METRICS.find((m) => m.key === selectedMetric) ?? ACTIVITY_METRICS[0],
    [selectedMetric]
  );

  // Users actually plotted: chosen in the multi-select, still present, and (optionally) non-zero.
  const chartUsers = useMemo(
    () =>
      selectedUsers
        .filter((user) => users.includes(user))
        .filter(
          (user) =>
            !hideZero || getMetricValue(stats?.userActivity?.[user], metric.key) !== 0
        ),
    [selectedUsers, users, hideZero, stats, metric]
  );

  const data = useMemo(
    () => ({
      labels: chartUsers,
      datasets: [
        {
          label: metric.label,
          data: chartUsers.map((user) => getMetricValue(stats?.userActivity?.[user], metric.key)),
          backgroundColor: primaryColor,
          borderRadius: 4,
          maxBarThickness: 64,
        },
      ],
    }),
    [chartUsers, stats, primaryColor, metric]
  );

  const selectionLabel =
    chartUsers.length === 0
      ? "No users"
      : chartUsers.length === users.length
      ? "All users"
      : `${chartUsers.length} users`;

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 flex w-full items-start justify-between gap-4">
          <div className="prose min-w-0">
            <h3 className="mb-0 flex items-center">
              <ChartBarIcon className="inline-block w-6 h-6 mr-2 text-gray-500" />
              {title}
            </h3>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Hide zeroes
            </label>

            <Listbox value={selectedMetric} onChange={onMetricChange}>
              <div className="relative w-48">
                <ListboxButton className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <span className="block truncate">{metric.label}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute right-0 z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {ACTIVITY_METRICS.map((option) => (
                      <ListboxOption
                        key={option.key}
                        value={option.key}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                            active ? "bg-primary-100 text-gray-900" : "text-gray-900"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                              {option.label}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </div>
            </Listbox>

            <Listbox value={selectedUsers} onChange={setSelectedUsers} multiple>
              <div className="relative w-56">
                <ListboxButton className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <span className="block truncate">{selectionLabel}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute right-0 z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {users.length === 0 ? (
                      <div className="px-4 py-2 text-gray-400">No users</div>
                    ) : (
                      users.map((user) => (
                        <ListboxOption
                          key={user}
                          value={user}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? "bg-primary-100 text-gray-900" : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                {user}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      ))
                    )}
                  </ListboxOptions>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

        {isLoading ? (
          <div className="mt-6 min-h-[200px]">
            <LoadingWheel />
          </div>
        ) : chartUsers.length === 0 ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            No users selected.
          </div>
        ) : (
          <div className="mt-6 h-96">
            <Bar options={options} data={data} />
          </div>
        )}
      </div>
    </DashboardTile>
  );
}
